import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import BomExplorerPage from "@/pages/BomExplorerPage";
import ProductionPlanPage from "@/pages/ProductionPlanPage";
import ProductionConfirmationPage from "@/pages/ProductionConfirmationPage";
import ConfirmedProductionPage from "@/pages/ConfirmedProductionPage";
import ProductionConfirmationTestPage from "@/pages/ProductionConfirmationTestPage";
import AdminPage from "@/pages/AdminPage";
import InventoryPage from "@/pages/InventoryPage";
import MovementAuditTrailPage from "@/pages/MovementAuditTrailPage";
import WipByStagePage from "@/pages/WipByStagePage";
import DeliveryNotePage from "@/pages/DeliveryNotePage";
import GatePassPage from "@/pages/GatePassPage";
import SapWritePage from "@/pages/SapWritePage";
import BomCopyPage from "@/pages/BomCopyPage";
import RoutingBuilderPage from "@/pages/RoutingBuilderPage";
import BooManagementPage from "@/pages/BooManagementPage";
import BooDetailPage from "@/pages/BooDetailPage";
import StoreApprovalPage from "@/pages/StoreApprovalPage";
import NpdStoreRequestPage from "@/pages/NpdStoreRequestPage";
import LoginPage from "@/pages/LoginPage";
import PendingAccessPage from "@/pages/PendingAccessPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import QualityDecisionPage from "@/pages/QualityDecisionPage";
import QualityActivityPage from "@/pages/QualityActivityPage";
import AccessManagementPage from "@/pages/AccessManagementPage";
import ListPriceInstructionsPage from "@/pages/ListPriceInstructionsPage";
import SapIntegrationsDocsPage from "@/pages/SapIntegrationsDocsPage";
import ToolingBudgetGuidePage from "@/pages/ToolingBudgetGuidePage";
import CostCenterConsumptionPage from "@/pages/CostCenterConsumptionPage";
import ConsumptionRegisterPage from "@/pages/ConsumptionRegisterPage";
import ConsumptionRegisterSetupPage from "@/pages/ConsumptionRegisterSetupPage";
import ByproductExemptionPage from "@/pages/ByproductExemptionPage";
import StoreIssuedItemsPage from "@/pages/StoreIssuedItemsPage";
import MachineMasterPage from "@/pages/MachineMasterPage";
import PhysicalInventoryPage from "@/pages/PhysicalInventoryPage";
import SapCredentialHealthPage from "@/pages/SapCredentialHealthPage";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Footer } from "@/components/Footer";
import { ConcurrencyBadge } from "@/components/ConcurrencyBadge";
import { Toaster } from "@/components/ui/sonner";
import { CircleNotch } from "@phosphor-icons/react";

// Sep 2026 bug fix: the root URL rendered a stark blank white page for as
// long as `/api/auth/me` took to respond - AuthGate returned `null` while
// `loading` was true, and the backend can genuinely take several seconds
// (sometimes longer, occasionally a gateway 502) to respond under live SAP
// call load. A real spinner here means the user always sees SOMETHING
// instead of a blank tab that looks broken/crashed.
function AuthLoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]" data-testid="auth-loading-screen">
      <CircleNotch size={28} weight="bold" className="animate-spin text-[#0E7C86]" />
    </div>
  );
}

// Aug 2026: Store Approval now requires Entra ID login like every other
// page (was previously exempted here) - user's explicit ask, now that
// the actor's name comes from the signed-in session, not a manual box.
function AuthGate({ children }) {
  const { user, loading, isPendingAccess } = useAuth();

  if (loading) return <AuthLoadingScreen />;
  // User's explicit ask (Sep 2026): build-version footer must show on
  // EVERY page - Login/Pending-Access used to bypass the layout wrapper
  // that carries <Footer/> below, so they never got it.
  if (!user) return <div className="min-h-screen flex flex-col"><div className="flex-1"><LoginPage /></div><Footer /></div>;
  // Oct 26 2026: a floor login still carrying the password his admin typed
  // reaches nothing until he has set his own (enforced in the backend
  // middleware too, so this is the screen for it rather than the gate).
  if (user.must_change_password) {
    return <div className="min-h-screen flex flex-col"><div className="flex-1"><ChangePasswordPage /></div><Footer /></div>;
  }
  if (isPendingAccess) return <div className="min-h-screen flex flex-col"><div className="flex-1"><PendingAccessPage /></div><Footer /></div>;
  return children;
}

// Aug 2026 bug fix: "/" was hard-gated to bom_explorer, so a user granted
// ONLY a different page (e.g. just "inventory") landed on their bookmarked
// home URL and hit a hard "Access Denied" even though they legitimately
// have access to part of the app - isPendingAccess above doesn't catch
// this since it only fires when allowed_pages is completely empty. Send
// them straight to the first page they actually have instead.
const FIRST_ACCESSIBLE_PAGE_ROUTES = [
  ["production_plan", "/production-plan"],
  ["production_confirmation", "/production-confirmation"],
  // Sep 15 2026, same bug-fix pattern: a user granted ONLY the Test right
  // (e.g. Sudheer Singh) landed on "No Pages Granted Yet" instead of the
  // one page they actually have.
  ["production_confirmation_test", "/admin/production-confirmation-test"],
  ["inventory", "/inventory"],
  // Oct 2026: a shop-floor operator is typically granted ONLY this page.
  ["consumption_register", "/consumption-register"],
  ["admin", "/admin"],
  ["admin_sap_write", "/admin/sap-write"],
  ["admin_bom_copy", "/admin/bom-copy"],
  ["admin_boo", "/admin/boo"],
  ["admin_routing_builder", "/admin/routing-builder"],
  ["machine_master", "/admin/machine-master"],
  ["store_approval", "/storeapproval"],
  ["npd_store_request", "/npd-requests/store"],
  // Jun 2026: a tooling approver may hold ONLY one of these - the NPD page's
  // Tooling Development section is then the first thing they can open.
  ["npd_tooling_head_approval", "/npd-requests/approvals"],
  ["npd_tooling_management_approval", "/npd-requests/approvals"],
  ["movement_audit_trail", "/inventory/movement-audit-trail"],
  ["wip_by_stage", "/inventory/wip-by-stage"],
  ["physical_inventory", "/physical-inventory"],
];

function HomeRoute() {
  const { hasPageAccess } = useAuth();
  if (hasPageAccess("bom_explorer")) return <BomExplorerPage />;
  const firstAccessible = FIRST_ACCESSIBLE_PAGE_ROUTES.find(([page]) => hasPageAccess(page));
  if (firstAccessible) return <Navigate to={firstAccessible[1]} replace />;
  return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-4" data-testid="no-accessible-pages">
      <div className="text-center">
        <h1 className="font-heading text-lg font-bold text-[#101828]">No Pages Granted Yet</h1>
        <p className="text-sm text-[#667085] mt-1">Ask your IT admin to grant you access to a page.</p>
      </div>
    </div>
  );
}

function InternalApp() {
  return (
    <AuthGate>
      <div className="min-h-screen flex flex-col">
        <div className="flex-1">
          <Routes>
            <Route path="/" element={<HomeRoute />} />
            <Route path="/production-plan" element={<ProtectedRoute page="production_plan"><ProductionPlanPage /></ProtectedRoute>} />
            <Route path="/production-confirmation" element={<ProtectedRoute page="production_confirmation"><ProductionConfirmationPage /></ProtectedRoute>} />
            <Route path="/quality-decision" element={<ProtectedRoute anyOf={["quality_decision", "production_confirmation"]}><QualityDecisionPage /></ProtectedRoute>} />
            <Route path="/admin/quality-activity" element={<ProtectedRoute superAdminOnly><QualityActivityPage /></ProtectedRoute>} />
            <Route path="/production-confirmation/confirmed-report" element={<ProtectedRoute page="production_confirmation"><ConfirmedProductionPage /></ProtectedRoute>} />
            {/* Sep 2026, user's explicit ask: test page for multi-Reporting-Point
                models (RP10/RP20/END style) is now its own grantable right
                (see PAGE_CATALOG's "production_confirmation_test") instead of
                superAdminOnly, so it can be assigned to a regular user. */}
            <Route path="/admin/production-confirmation-test" element={<ProtectedRoute page="production_confirmation_test"><ProductionConfirmationTestPage /></ProtectedRoute>} />
            {/* Oct 2026, user's approved plan-mode spec: Consumption Register for
                chemicals & consumables. Own grantable right; the master data
                lives on its own admin-only Setup page below. */}
            <Route path="/consumption-register" element={<ProtectedRoute page="consumption_register"><ConsumptionRegisterPage /></ProtectedRoute>} />
            <Route path="/admin/consumption-register-setup" element={<ProtectedRoute superAdminOnly><ConsumptionRegisterSetupPage /></ProtectedRoute>} />
            <Route path="/inventory" element={<ProtectedRoute page="inventory"><InventoryPage /></ProtectedRoute>} />
            {/* Oct 3 2026, user's explicit ask: the "Inter Plant Stock Transfer"
                and "Inbound STO Receipt" PAGES are removed - nav entries gone and
                these routes no longer exist, so a direct URL 404s. Their backend
                endpoints, data and access checkboxes are untouched. The two
                print-only documents below are deliberately kept reachable by URL
                so existing STO paperwork can still be printed. */}
            <Route path="/inventory/inter-plant-transfer/:stoId/delivery-note" element={<ProtectedRoute page="stock_transfer"><DeliveryNotePage /></ProtectedRoute>} />
            <Route path="/inventory/inter-plant-transfer/:stoId/gate-pass" element={<ProtectedRoute page="stock_transfer"><GatePassPage /></ProtectedRoute>} />
            {/* Jun 2026, user's explicit ask ("allow movement audit to all user"):
                read-only ledger, any signed-in user - no page right needed. */}
            <Route path="/inventory/movement-audit-trail" element={<ProtectedRoute anyUser><MovementAuditTrailPage /></ProtectedRoute>} />
            <Route path="/inventory/wip-by-stage" element={<ProtectedRoute page="wip_by_stage"><WipByStagePage /></ProtectedRoute>} />
            {/* Oct 21 2026, user's approved spec: Physical Inventory count ->
                adjustment request -> admin confirms inside the Cost Center
                Consumption page. Own grantable right. */}
            <Route path="/physical-inventory" element={<ProtectedRoute page="physical_inventory"><PhysicalInventoryPage /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute page="admin"><AdminPage /></ProtectedRoute>} />
            <Route path="/admin/sap-write" element={<ProtectedRoute page="admin_sap_write"><SapWritePage /></ProtectedRoute>} />
            <Route path="/admin/bom-copy" element={<ProtectedRoute page="admin_bom_copy"><BomCopyPage /></ProtectedRoute>} />
            <Route path="/admin/boo" element={<ProtectedRoute page="admin_boo"><BooManagementPage /></ProtectedRoute>} />
            <Route path="/admin/routing-builder" element={<ProtectedRoute page="admin_routing_builder"><RoutingBuilderPage /></ProtectedRoute>} />
            <Route path="/admin/boo/:booId" element={<ProtectedRoute page="admin_boo"><BooDetailPage /></ProtectedRoute>} />
            <Route path="/admin/access-management" element={<ProtectedRoute superAdminOnly><AccessManagementPage /></ProtectedRoute>} />
            {/* Sep 29 2026, user's explicit ask ("restricted to Administrator role")
                - same superAdminOnly (admin/super_admin) gate as Access Management,
                deliberately NOT a PAGE_CATALOG-assignable permission. */}
            <Route path="/admin/cost-center-consumption" element={<ProtectedRoute superAdminOnly><CostCenterConsumptionPage /></ProtectedRoute>} />
            <Route path="/admin/byproduct-exemption" element={<ProtectedRoute superAdminOnly><ByproductExemptionPage /></ProtectedRoute>} />
            <Route path="/admin/store-issued-items" element={<ProtectedRoute superAdminOnly><StoreIssuedItemsPage /></ProtectedRoute>} />
            <Route path="/admin/machine-master" element={<ProtectedRoute page="machine_master"><MachineMasterPage /></ProtectedRoute>} />
            <Route path="/admin/sap-credentials" element={<ProtectedRoute superAdminOnly><SapCredentialHealthPage /></ProtectedRoute>} />
            <Route path="/storeapproval" element={<ProtectedRoute page="store_approval"><StoreApprovalPage /></ProtectedRoute>} />
            <Route path="/storeapproval/journal" element={<ProtectedRoute page="store_approval"><StoreApprovalPage /></ProtectedRoute>} />
            <Route path="/storeapproval/balance" element={<ProtectedRoute page="store_approval"><StoreApprovalPage /></ProtectedRoute>} />
            <Route path="/storeapproval/movements" element={<ProtectedRoute page="store_approval"><StoreApprovalPage /></ProtectedRoute>} />
            <Route path="/storeapproval/returns" element={<ProtectedRoute page="store_approval"><StoreApprovalPage /></ProtectedRoute>} />
            <Route path="/storeapproval/request/:requestId" element={<ProtectedRoute page="store_approval"><StoreApprovalPage /></ProtectedRoute>} />
            {/* Oct 2026, user's explicit spec: NPD Store Request - manually
                raised (site + purpose + item codes), lands in the same store
                queue as every other request. Own grantable right. */}
            {/* Jun 2026, user's ask: NPD is its own top-level menu, so every NPD
                section has its own URL and the menu deep-links into it. The old
                single route stays for existing bookmarks. */}
            <Route path="/npd-store-request" element={<ProtectedRoute anyOf={["npd_store_request", "npd_tooling_head_approval", "npd_tooling_management_approval"]}><NpdStoreRequestPage /></ProtectedRoute>} />
            <Route path="/npd-requests" element={<ProtectedRoute anyOf={["npd_store_request", "npd_tooling_head_approval", "npd_tooling_management_approval"]}><NpdStoreRequestPage /></ProtectedRoute>} />
            <Route path="/npd-requests/:section" element={<ProtectedRoute anyOf={["npd_store_request", "npd_tooling_head_approval", "npd_tooling_management_approval"]}><NpdStoreRequestPage /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
        <Footer />
        <ConcurrencyBadge />
      </div>
    </AuthGate>
  );
}

function AppShell() {
  const { pathname } = useLocation();
  // Public, no-login work instruction page - shareable URL for any staff
  // member, must never be wrapped by AuthGate's Microsoft sign-in.
  if (pathname === "/list-price-instructions") {
    return <ListPriceInstructionsPage />;
  }
  // Public, no-login SAP SOAP/OData integration reference (user's
  // explicit ask, Sep 18 2026) - same bypass pattern as above.
  if (pathname === "/docs/sap-integrations") {
    return <SapIntegrationsDocsPage />;
  }
  // Public, no-login NPD tooling budget flow guide (user's ask, Jun 2026:
  // "create a basic instructions flow url ... make a full flow on open url to
  // illustrate to users") - same bypass pattern as above.
  if (pathname === "/docs/tooling-budget-flow") {
    return <ToolingBudgetGuidePage />;
  }
  return <InternalApp />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
      <Toaster />
    </AuthProvider>
  );
}

export default App;
